import React from 'react'
import { useNavigate } from 'react-router-dom'
import useSWRMutation from 'swr/mutation'

import config from '../../config'
import { removeInvoice } from '../../api'
import { useAlerts } from '../../context/alerts'
import { canSendInvoiceToClient } from '../../lib/invoiceDelivery'
import { Invoice, InvoiceStatus } from '../../types'
import Dropdown, { type DropdownOpenDirection } from '../ui/dropdown'
import { ButtonSize } from '../ui/button'
import { ReactComponent as InvoiceIcon } from '../../static/images/invoice.svg'
import { ReactComponent as SendIcon } from '../../static/images/send.svg'
import { ReactComponent as CloneIcon } from '../../static/images/clone.svg'
import { ReactComponent as TrashIcon } from '../../static/images/trash.svg'

type InvoiceActionsTarget = Pick<Invoice, '_id' | 'status' | 'total' | 'lastActivity' | 'sentManually'>

type Props = {
  invoice: InvoiceActionsTarget
  onDeleted?: () => void
  disabled?: boolean
  /** When false, hides Send Reminder for overdue invoices (e.g. when shown elsewhere). Default true. */
  showSendReminder?: boolean
  /** When false, hides Send to client / Send Reminder menu entries (e.g. bottom bar with primary Send). Default true. */
  showSendInMenu?: boolean
  showDelete?: boolean
  buttonSize?: ButtonSize
  viewPdfLabel?: string
  viewPdfDisabled?: boolean
  sendDisabled?: boolean
  onViewPdf?: (e: React.MouseEvent) => void
  onSendToClient?: (e: React.MouseEvent) => void
  onDuplicate?: (e: React.MouseEvent) => void
  onDelete?: (e: React.MouseEvent) => void
  openDirection?: DropdownOpenDirection
}

const InvoiceActionsDropdown: React.FC<Props> = ({
  invoice,
  onDeleted,
  disabled,
  showSendReminder = true,
  showSendInMenu = true,
  showDelete = true,
  buttonSize,
  viewPdfLabel = 'View PDF',
  viewPdfDisabled,
  sendDisabled,
  onViewPdf,
  onSendToClient,
  onDuplicate,
  onDelete,
  openDirection = 'down',
}) => {
  const navigate = useNavigate()
  const { addAlert } = useAlerts()
  const { trigger: triggerRemoveInvoice, isMutating } = useSWRMutation(
    `/api/invoices/${invoice._id}`,
    removeInvoice
  )

  const isBusy = disabled || isMutating
  const isInvoiceOverdue = invoice.status === InvoiceStatus.OVERDUE
  const isZeroTotalInvoice = !invoice.total || Number(invoice.total) === 0
  const sendReminderDisabled = isInvoiceOverdue && isZeroTotalInvoice
  const sendToClientEnabled = canSendInvoiceToClient(invoice)
  const showSendAction = showSendInMenu && (!isInvoiceOverdue || showSendReminder)

  const handleSendToClient = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (onSendToClient) {
      onSendToClient(e)
      return
    }
    navigate(`/invoices/${invoice._id}/send-email`)
  }

  const handleViewPdf = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (onViewPdf) {
      onViewPdf(e)
      return
    }
    window.open(`${config.REACT_APP_API_URL}/api/invoices/pdf/${String(invoice._id)}`, '_blank')
  }

  const handleDuplicate = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (onDuplicate) {
      onDuplicate(e)
      return
    }
    navigate(`/invoices/new?cloneId=${invoice._id}`)
  }

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (onDelete) {
      onDelete(e)
      return
    }
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      await triggerRemoveInvoice()
      addAlert({ type: 'info', message: 'Invoice deleted' })
      onDeleted?.()
    }
  }

  return (
    <div className='invoice-actions-item invoice-actions-item--icon-only' onClick={(e) => e.stopPropagation()}>
      <Dropdown buttonLabel='' buttonSize={buttonSize} openDirection={openDirection}>
        <button type='button' onClick={handleViewPdf} disabled={isBusy || viewPdfDisabled}>
          <InvoiceIcon width={16} height={16} aria-hidden />
          {viewPdfLabel}
        </button>
        {showSendAction && (
          isInvoiceOverdue ? (
            <button
              type='button'
              onClick={handleSendToClient}
              disabled={isBusy || sendDisabled || sendReminderDisabled}
            >
              <SendIcon width={16} height={16} aria-hidden />
              Send Reminder
            </button>
          ) : (
            <button
              type='button'
              onClick={handleSendToClient}
              disabled={isBusy || sendDisabled || !sendToClientEnabled}
            >
              <SendIcon width={16} height={16} aria-hidden />
              Send to client
            </button>
          )
        )}
        <button type='button' onClick={handleDuplicate} disabled={isBusy}>
          <CloneIcon width={16} height={16} aria-hidden />
          Duplicate invoice
        </button>
        {showDelete && (
          <button type='button' onClick={handleDelete} disabled={isBusy} data-destructive>
            <TrashIcon width={16} height={16} aria-hidden />
            Delete invoice
          </button>
        )}
      </Dropdown>
    </div>
  )
}

export default InvoiceActionsDropdown
